/**
 * Wire types for thought endorsements (`POST` / `DELETE /ajax/endorsement/{thoughtPublicId}`).
 *
 * Hand-written rather than generated, per AGENTS.md rule 6: the endpoints are new and
 * `make sync-client-local` needs a running local backend plus a devo tunnel. Consumption still goes
 * through `ClientFactory`, so the rule holds. These shapes mirror `EndorseThoughtRequest` and
 * `ThoughtEndorsementSummaryDTO`.
 *
 * ⚠️ **The path is `/ajax/endorsement/{thoughtPublicId}`, read from `AjaxEndorsementRestController`'s
 * class-level `@RequestMapping("/ajax/endorsement")` plus its method mappings.** The design doc's §13.4
 * happens to agree, but the controller is the only artefact that IS the mapping; a plan is intent.
 *
 * Design: `designs/00-inbox/2026-08-25-profile-endorsements-and-the-appreciation-wall-design.md`
 * §7 (E1–E14), §8, §9.1, §13.4, §7.15.
 */

/**
 * The closed vocabulary (design §8.2). The KEY is what travels on the wire and is written to the
 * column; it is never renamed. The display copy is `discussion.endorsement.kind.<lower-case key>` and
 * may be re-worded freely.
 *
 * ⚠️ **This array is PRESENTATION order, and it deliberately differs from `EndorsementKind.java`'s
 * declaration order, which is REGISTRY order.** The server returns a thought's kinds in registry
 * order (an `EnumSet` iteration) and `toEndorsementRecord` re-sorts them through this array, so the
 * menu and the chip both read in the order below. Keeping one array for both jobs is what would force
 * the choice between "new kinds at the bottom of the menu" and "renumbering storage order"; keeping
 * them separate costs nothing, because nothing on the wire depends on this order.
 *
 * **Ordered by fit to the corpus, not by age** (design §8.2 amendment, 2026-09-09). `WELL_SAID` leads
 * because it is 74.5% of all endorsements given; `MOVED_ME` is second because the corpus is
 * overwhelmingly poetry and it is the kind that should be taking `WELL_SAID`'s overflow;
 * `FAIR_TO_THE_OTHER_SIDE` is last because only 18.6% of posts attract a single disagreeing voter and
 * it accounts for 4.7% of use. It is last, not gone: it draws the longest notes in the product.
 *
 * ⚠️ `TAUGHT_ME` must stay ahead of `FAIR_TO_THE_OTHER_SIDE` here — `EndorsementControl.spec.tsx` pins
 * a chip's `data-endorsement-kinds` as `"TAUGHT_ME,FAIR_TO_THE_OTHER_SIDE"`, which is an assertion
 * about THIS array's order.
 *
 * **Seven kinds is 200 catalog entries and it still has to fit a 390px action row.** Two mechanics
 * pay for that and both are load-bearing: the control is a `CtaButtonDropdown`, so extra kinds cost
 * dropdown height and no row width; and `EndorsementControl` caps the chip's inline kind list at
 * `CHIP_INLINE_KIND_LIMIT` names plus a `+N`, because a well-received poem can now carry seven.
 */
export const ENDORSEMENT_KINDS = [
    "WELL_SAID",
    "MOVED_ME",
    "TAUGHT_ME",
    "STAYED_WITH_ME",
    "KEPT_ME_READING",
    "PUT_IT_INTO_WORDS",
    "FAIR_TO_THE_OTHER_SIDE",
] as const;

export type EndorsementKind = (typeof ENDORSEMENT_KINDS)[number];

export function isEndorsementKind(value: unknown): value is EndorsementKind {
    return typeof value === "string" && (ENDORSEMENT_KINDS as readonly string[]).includes(value);
}

/** `POST /ajax/endorsement/{thoughtPublicId}`. The kind is required (E4); the note is optional (E5). */
export type EndorseThoughtRequest = {
    kind: EndorsementKind;
    /** Optional, 280 characters. Blank means "no note" and never clears an existing note. */
    note?: string;
};

/**
 * What the chip needs, and nothing else — the server's `ThoughtEndorsementSummaryDTO`. Note text is
 * NOT here by construction (E6: it renders only on the `noindex` notes page of phase 3).
 */
export type ThoughtEndorsementSummary = {
    /** Live endorsements on this thought. One per giver, so this IS the distinct-endorser count. */
    count: number;
    /** The distinct kinds present, in registry order. */
    kinds: EndorsementKind[];
    /** The viewer's own kind, or null when they have not endorsed (or are anonymous). */
    viewerKind?: EndorsementKind | null;
    /** Whether the viewer's own endorsement carries a note. */
    viewerHasNote?: boolean;
};

/**
 * The per-thought field the thought DTOs carry, read through a narrow cast the same way
 * `ThoughtMindChangedFields` is (rule 6 — the backend gained it without a client regeneration).
 *
 * `null`/absent means "not resolved for this projection" (the reply tree's producer does not fill it
 * yet, design §7.15 decision 3): render no chip, keep the control. It is NOT "zero endorsements" —
 * that arrives as `{count: 0, kinds: []}`.
 */
export type ThoughtEndorsementFields = {
    endorsements?: ThoughtEndorsementSummary | null;
};

/**
 * The FLAT record the viewer-action registry keeps for a thought (`"thought-endorsement"`).
 *
 * Flat on purpose: `ViewerActionRecords` compares records key by key with `Object.is`, so an array
 * inside the payload would never compare equal to its wire-rebuilt twin and every remount would
 * re-render. `kindsCsv` is the `kinds` array joined with `,` in registry order.
 */
export type ThoughtEndorsementRecord = {
    count: number;
    kindsCsv: string;
    viewerKind: EndorsementKind | null;
    viewerHasNote: boolean;
};

export function toEndorsementRecord(summary: ThoughtEndorsementSummary | null | undefined): ThoughtEndorsementRecord | null {
    if (!summary) {
        return null;
    }
    const kinds = (summary.kinds ?? []).filter(isEndorsementKind);
    return {
        count: Math.max(0, Number(summary.count ?? 0) || 0),
        kindsCsv: ENDORSEMENT_KINDS.filter((kind) => kinds.includes(kind)).join(","),
        viewerKind: isEndorsementKind(summary.viewerKind) ? summary.viewerKind : null,
        viewerHasNote: Boolean(summary.viewerHasNote),
    };
}

export function kindsOfRecord(record: ThoughtEndorsementRecord | null | undefined): EndorsementKind[] {
    if (!record || !record.kindsCsv) {
        return [];
    }
    return record.kindsCsv.split(",").filter(isEndorsementKind);
}

/** A stable machine code the server puts on a refused write; the client owns the copy for each. */
export type EndorsementRefusalCode =
    | "thought_not_found"
    | "own_thought"
    | "blocked"
    | "account_restricted"
    | "kind_required"
    | "note_too_long"
    | "note_refused"
    | "not_endorsed";

export function readEndorsementRefusalCode(error: unknown): EndorsementRefusalCode | null {
    const body = (error as any)?.response?.data ?? null;
    const code = body && typeof body.code === "string" ? body.code : null;
    switch (code) {
        case "thought_not_found":
        case "own_thought":
        case "blocked":
        case "account_restricted":
        case "kind_required":
        case "note_too_long":
        case "note_refused":
        case "not_endorsed":
            return code;
        default:
            return null;
    }
}
